/* ──────────────────────────────────────────────────────────────────────
   AiPage — /ai/ AI for BIM & digital engineering page
   Story-led: hero (light) → proof (navy) → audiences (aqua-wash) →
   engagement (navy) → closing CTA inside engagement → footer.
   Namespace: .dz-ai-*
   ────────────────────────────────────────────────────────────────────── */

const { useState: useAiState, useEffect: useAiEffect, useRef: useAiRef } = React;

// Path-based AR localization for hardcoded UI labels (content from content/ai_ar.js).
function dzIsAr() { return typeof window !== 'undefined' && window.location.pathname.startsWith('/ar/'); }
function dzT(en, ar) { return dzIsAr() ? ar : en; }

function useAiReveal(threshold) {
  const ref = useAiRef(null);
  useAiEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { el.classList.add('is-in-view'); io.disconnect(); }
      });
    }, { threshold });
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return ref;
}

/* ── Count-up component for proof metrics ────────────────────────────── */
function AiCountNumeral({ value, className }) {
  const ref = useAiRef(null);
  const m = typeof value === 'string'
    ? value.match(/^(\D*)(\d+(?:\.\d+)?)(.*)$/)
    : null;
  const isNumeric = !!m;
  const prefix    = isNumeric ? m[1] : '';
  const target    = isNumeric ? parseFloat(m[2]) : 0;
  const decimals  = isNumeric && m[2].includes('.') ? m[2].split('.')[1].length : 0;
  const suffix    = isNumeric ? m[3] : '';

  // SSR / no-JS / pre-scroll must show the FINAL value (e.g. 42, 70%, 10 min), not 0.
  // The count-up below is progressive enhancement only (H1/D1).
  const initial = value;
  const [display, setDisplay] = useAiState(initial);

  useAiEffect(() => {
    if (!isNumeric || target === 0) return;
    const el = ref.current;
    if (!el) return;
    // Progressive enhancement only: under reduced-motion, keep the final value (no count-up).
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          io.disconnect();
          const duration = 1400;
          const t0 = performance.now();
          const tick = (now) => {
            const t = Math.min(1, (now - t0) / duration);
            const eased = 1 - Math.pow(1 - t, 3);
            const current = target * eased;
            const formatted = decimals > 0
              ? current.toFixed(decimals)
              : String(Math.round(current));
            setDisplay(`${prefix}${formatted}${suffix}`);
            if (t < 1) requestAnimationFrame(tick);
            else setDisplay(`${prefix}${decimals > 0 ? target.toFixed(decimals) : target}${suffix}`);
          };
          requestAnimationFrame(tick);
        }
      });
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [value]);

  return <span ref={ref} className={className || 'dz-ai-proof__num'}>{display}</span>;
}

/* ── Workflow River — animated horizontal pipeline ───────────────────── */
function AiWorkflowRiver() {
  const stations = dzIsAr() ? [
    { id: 'CLH', name: 'التنسيق',       sub: '8 مسارات'  },
    { id: 'QTO', name: 'حصر الكميات',   sub: '12 مساراً' },
    { id: 'DOC', name: 'التوثيق',       sub: '10 مسارات' },
    { id: 'QA',  name: 'فحص النموذج',   sub: '12 مساراً' }
  ] : [
    { id: 'CLH', name: 'Coordination',  sub: '8 workflows'  },
    { id: 'QTO', name: 'Takeoff',       sub: '12 workflows' },
    { id: 'DOC', name: 'Documentation', sub: '10 workflows' },
    { id: 'QA',  name: 'Model QA',      sub: '12 workflows' }
  ];
  const tasks = dzIsAr() ? [
    { stage: 'CLH', text: 'دفعة تعارضات · مُرتَّبة حسب الأولوية' },
    { stage: 'QTO', text: 'حصر الكميات · جدول مُصدَّر' },
    { stage: 'DOC', text: 'طلب معلومات (RFI) · مُوجَّه للمراجعة' },
    { stage: 'QA',  text: 'فحص النموذج · التسمية وLOD مُدقَّقتان' },
    { stage: 'CLH', text: 'جولة تنسيق · مُغلَقة قبل المناقصة' },
    { stage: 'DOC', text: 'سجلّ التقديمات · مُولَّد آلياً' },
    { stage: 'QTO', text: 'جدول الكميات · مُطابَق للنموذج المُوحَّد' },
    { stage: 'QA',  text: 'النموذج المُوحَّد · مُتحقَّق منه وفق EIR' },
    { stage: 'DOC', text: 'مخططات GA · التعليقات مُزامَنة' },
    { stage: 'CLH', text: 'MEP مقابل الإنشاء · تعارضات مُعلَّمة' }
  ] : [
    { stage: 'CLH', text: 'Clash batch · triaged by priority' },
    { stage: 'QTO', text: 'Quantity takeoff · schedule exported' },
    { stage: 'DOC', text: 'RFI · routed for review' },
    { stage: 'QA',  text: 'Model QA · naming & LOD checked' },
    { stage: 'CLH', text: 'Coordination round · closed before tender' },
    { stage: 'DOC', text: 'Submittal log · auto-generated' },
    { stage: 'QTO', text: 'BoQ · matched to the federated model' },
    { stage: 'QA',  text: 'Federated model · validated against EIR' },
    { stage: 'DOC', text: 'GA drawings · annotations synced' },
    { stage: 'CLH', text: 'MEP vs structure · conflicts flagged' }
  ];

  const [count, setCount]         = useAiState(1247);
  const [activeIdx, setActiveIdx] = useAiState(0);
  const [recent, setRecent]       = useAiState([]);
  const taskIdxRef                = useAiRef(0);
  const uidRef                    = useAiRef(0);

  const pad = (n) => String(n).padStart(2, '0');
  const stamp = () => {
    const d = new Date();
    return pad(d.getHours()) + ':' + pad(d.getMinutes()) + ':' + pad(d.getSeconds());
  };

  /* Seed initial 4 entries on mount */
  useAiEffect(() => {
    const initial = [];
    for (let i = 0; i < 4; i++) {
      const t = tasks[i];
      uidRef.current += 1;
      initial.push({ ...t, time: stamp(), key: uidRef.current });
    }
    setRecent(initial.reverse());
    taskIdxRef.current = 4;
  }, []);

  /* Counter ticks */
  useAiEffect(() => {
    const id = setInterval(() => setCount((c) => c + 1), 1100);
    return () => clearInterval(id);
  }, []);

  /* Station highlight cycle */
  useAiEffect(() => {
    const id = setInterval(() => setActiveIdx((i) => (i + 1) % stations.length), 1500);
    return () => clearInterval(id);
  }, []);

  /* Emit a new completed task every 2.2s */
  useAiEffect(() => {
    const id = setInterval(() => {
      const t = tasks[taskIdxRef.current % tasks.length];
      taskIdxRef.current += 1;
      uidRef.current += 1;
      const entry = { ...t, time: stamp(), key: uidRef.current };
      setRecent((r) => [entry, ...r].slice(0, 4));
    }, 2200);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="dz-ai-river">
      <div className="dz-ai-river__header">
        <div className="dz-ai-river__title">
          <strong>{dzT('DZ · BIM Workflow Pipeline', 'DZ · مسار عمل BIM')}</strong> · {dzT('LIVE', 'مباشر')}
        </div>
        <div className="dz-ai-river__counter">
          <span className="dz-ai-river__counter-num">{count.toLocaleString()}</span>
          <span className="dz-ai-river__counter-lbl">{dzT('tasks · 24h', 'مهمّة · 24 ساعة')}</span>
        </div>
      </div>

      <div className="dz-ai-river__stations">
        <span className="dz-ai-river__rail"      aria-hidden="true" />
        <span className="dz-ai-river__rail-glow" aria-hidden="true" />
        <div  className="dz-ai-river__particles" aria-hidden="true">
          <span className="dz-ai-river__particle" style={{ animationDelay: '0s' }} />
          <span className="dz-ai-river__particle" style={{ animationDelay: '1.6s' }} />
          <span className="dz-ai-river__particle" style={{ animationDelay: '3.2s' }} />
          <span className="dz-ai-river__particle" style={{ animationDelay: '4.8s' }} />
        </div>
        {stations.map((s, i) => (
          <div
            className={`dz-ai-river__station ${i === activeIdx ? 'is-active' : ''}`}
            key={s.id}
          >
            <span className="dz-ai-river__circle">{s.id}</span>
            <span className="dz-ai-river__station-name">{s.name}</span>
            <span className="dz-ai-river__station-sub">{s.sub}</span>
          </div>
        ))}
      </div>

      <div className="dz-ai-river__list">
        <div className="dz-ai-river__list-head">{dzT('Recently completed', 'أُنجزت حديثاً')}</div>
        <div className="dz-ai-river__list-items">
          {recent.map((t) => (
            <div className="dz-ai-river__item" key={t.key}>
              <span className="dz-ai-river__item-pill">{t.stage}</span>
              <span className="dz-ai-river__item-text">{t.text}</span>
              <span className="dz-ai-river__item-time">{t.time}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── HERO ─────────────────────────────────────────────────────────────── */
function AiHero({ data }) {
  const ref = useAiReveal(0.05);
  return (
    <section className="dz-ai-hero" ref={ref}>
      <div className="dz-ai-hero__inner">
        <div className="dz-ai-hero__copy">
          <span className="dz-eyebrow dz-tms-eyebrow">{data.eyebrow}</span>
          <h1 className="dz-ai-hero__title">
            <span className="dz-ai-hero__title-lead">{data.title.lead}</span>
            <span className="dz-ai-hero__title-accent">{data.title.accent}</span>
          </h1>
          <p className="dz-ai-hero__sub">{data.sub}</p>
          {data.statline && (
            <ul className="dz-ai-hero__stats">
              {data.statline.map((s, i) => (
                <li key={i} style={{ '--ai-i': i }}>{s}</li>
              ))}
            </ul>
          )}
          {data.ctas && (
            <div className="dz-ai-hero__cta">
              {data.ctas.map((c, i) => (
                <a key={i} className={'dz-btn ' + (c.outline ? 'dz-btn--profile-outline' : 'dz-btn--aqua')} href={c.href}>{c.label}</a>
              ))}
            </div>
          )}
        </div>
        <div className="dz-ai-hero__visual">
          <AiWorkflowRiver />
        </div>
      </div>
    </section>
  );
}

/* ── JOURNEY — 6-step roadmap on a sine-wave road ───────────────────── */
function AiJourneyIcon({ name }) {
  /* Badge icons — line-art, aqua stroke, all 24x24 viewBox-style centered at (0,0) */
  const c = { stroke: '#4FC8F4', strokeWidth: '2.2', fill: 'none',
              strokeLinecap: 'round', strokeLinejoin: 'round' };
  switch (name) {
    case 'magnifier': return (
      <g {...c} transform="translate(35 35)">
        <circle cx="-3" cy="-3" r="7" />
        <path d="M 3 3 L 9 9" />
      </g>
    );
    case 'compass': return (
      <g transform="translate(35 35) rotate(180)">
        <g {...c}>
          <circle cx="0" cy="0" r="10" />
          <path d="M 0 -9 L 3 0 L 0 9 L -3 0 Z" fill="#4FC8F4" opacity="0.5" />
          <circle cx="0" cy="0" r="1.4" fill="#4FC8F4" />
        </g>
      </g>
    );
    case 'flag': return (
      <g {...c} transform="translate(35 35)">
        <line x1="-7" y1="-11" x2="-7" y2="11" />
        <path d="M -7 -10 L 8 -10 L 4 -5 L 8 0 L -7 0 Z" fill="#4FC8F4" opacity="0.4" />
      </g>
    );
    case 'lightbulb': return (
      <g transform="translate(35 35) rotate(180)">
        <g {...c}>
          <path d="M 0 -9 C -5 -9 -8 -6 -8 -2 C -8 1 -7 2 -6 3 L -6 6 L 6 6 L 6 3 C 7 2 8 1 8 -2 C 8 -6 5 -9 0 -9 Z" />
          <line x1="-4" y1="9" x2="4" y2="9" />
          <line x1="-3" y1="11" x2="3" y2="11" />
        </g>
      </g>
    );
    case 'rocket': return (
      <g {...c} transform="translate(35 35)">
        <path d="M 0 -11 C 4 -7 6 -3 6 1 L 6 8 L -6 8 L -6 1 C -6 -3 -4 -7 0 -11 Z" />
        <circle cx="0" cy="-3" r="1.6" fill="#4FC8F4" />
        <path d="M -6 8 L -8 11 M 6 8 L 8 11" opacity="0.6" />
      </g>
    );
    case 'pulse': return (
      <g transform="translate(35 35) rotate(180)">
        <g {...c} strokeWidth="2">
          <rect x="-12" y="-7" width="24" height="13" rx="1.8" />
          <path d="M -10 -1 L -5 -1 L -3 -5 L 0 4 L 3 -3 L 6 -1 L 10 -1" />
        </g>
      </g>
    );
    default: return null;
  }
}

function AiJourneyBadge({ step, idx }) {
  return (
    <div className={`dz-aij__badge ${step.side === 'below' ? 'dz-aij__badge--down' : ''}`}>
      <svg className="dz-aij__badge-svg" viewBox="0 0 70 88">
        <path d="M 27 60 L 43 60 L 35 80 Z" fill="#355678" />
        <circle className="dz-aij__halo" cx="35" cy="35" r="33" fill="none" stroke="#4FC8F4" strokeWidth="1" opacity="0.5" />
        <circle cx="35" cy="35" r="29" fill="#FFFFFF" stroke="url(#dz-aij-pin-grad)" strokeWidth="3" />
        <AiJourneyIcon name={step.icon} />
      </svg>
    </div>
  );
}

function AiJourney({ data }) {
  const ref = useAiReveal(0.15);
  return (
    <section className="dz-aij" ref={ref}>
      <header className="dz-aij__head">
        <span className="dz-eyebrow dz-tms-eyebrow">{data.eyebrow}</span>
        <h2 className="dz-aij__title">
          {data.title.lead} <em>{data.title.accent}</em>
        </h2>
        {data.sub && <p className="dz-aij__sub">{data.sub}</p>}
      </header>

      <div className="dz-aij__road">
        {/* Road SVG (hidden on mobile via CSS) */}
        <svg className="dz-aij__svg" viewBox="0 0 1000 460" preserveAspectRatio="none" aria-hidden="true">
          <defs>
            <linearGradient id="dz-aij-road-grad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"  stopColor="#3A5A7A" />
              <stop offset="100%" stopColor="#1F3855" />
            </linearGradient>
            <linearGradient id="dz-aij-pin-grad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor="#4FC8F4" />
              <stop offset="55%"  stopColor="#5BA5C8" />
              <stop offset="100%" stopColor="#355678" />
            </linearGradient>
            <path id="dz-aij-path"
                  d="M -20 180 C 40 180, 40 260, 100 260 C 160 260, 160 180, 220 180 C 280 180, 280 260, 340 260 C 400 260, 400 180, 460 180 C 520 180, 520 260, 580 260 C 640 260, 640 180, 700 180 C 760 180, 760 260, 820 260 C 880 260, 880 180, 940 180 C 1000 180, 1000 260, 1060 260" />
          </defs>

          <use className="dz-aij__road-shadow" href="#dz-aij-path"
               fill="none" stroke="rgba(53,86,120,0.10)" strokeWidth="64" strokeLinecap="round" />

          <use className="dz-aij__road-path" href="#dz-aij-path"
               fill="none" stroke="url(#dz-aij-road-grad)" strokeWidth="50" strokeLinecap="round" />

          <use className="dz-aij__road-highlight" href="#dz-aij-path"
               fill="none" stroke="rgba(79,200,244,0.22)" strokeWidth="46" strokeLinecap="round" />

          <use className="dz-aij__road-line" href="#dz-aij-path"
               fill="none" stroke="#FFFFFF" strokeWidth="2" strokeDasharray="12 10" />

          <circle className="dz-aij__particle" r="3" fill="#4FC8F4">
            <animateMotion dur="9s" repeatCount="indefinite" rotate="auto" begin="1.6s">
              <mpath href="#dz-aij-path" />
            </animateMotion>
          </circle>
        </svg>

        {/* 6 steps — absolute on desktop, flow on mobile via CSS */}
        {data.steps.map((step, i) => (
          <div
            className={`dz-aij__step dz-aij__step--${i + 1}`}
            style={{ '--aij-x': step.x + '%' }}
            key={i}
          >
            <div className="dz-aij__desc">
              <span className="dz-aij__kicker">{dzT('Step', 'الخطوة')} {step.idx}</span>
              <h5 className="dz-aij__name">{step.name}</h5>
              <p className="dz-aij__text">{step.text}</p>
            </div>
            <AiJourneyBadge step={step} idx={i} />
          </div>
        ))}
      </div>
    </section>
  );
}

/* ── PROOF — 4×2 bento of metrics ────────────────────────────────────── */
function AiProof({ data }) {
  const ref = useAiReveal(0.15);
  return (
    <section className="dz-ai-proof" ref={ref}>
      <div className="dz-ai-proof__head">
        <span className="dz-eyebrow dz-tms-eyebrow dz-tms-eyebrow--on-dark">{data.eyebrow}</span>
        <h2 className="dz-ai-h2 dz-ai-h2--on-dark">{data.title}</h2>
        <p className="dz-ai-lead dz-ai-lead--on-dark">{data.lead}</p>
      </div>
      <div className="dz-ai-proof__grid">
        {data.metrics.map((m, i) => (
          <article className="dz-ai-mcard" key={i} style={{ '--ai-i': i }}>
            <AiCountNumeral value={m.numeral} className="dz-ai-mcard__num" />
            <div className="dz-ai-mcard__label">{m.label}</div>
            {m.sub && <div className="dz-ai-mcard__sub">{m.sub}</div>}
          </article>
        ))}
      </div>
    </section>
  );
}

/* ── AUDIENCES — 4 cards in 2×2 (ghosted letter-mark) ────────────────── */
function AiAudiences({ data }) {
  const ref = useAiReveal(0.12);
  return (
    <section className="dz-ai-aud" ref={ref}>
      <div className="dz-ai-aud__head">
        <span className="dz-eyebrow dz-tms-eyebrow">{data.eyebrow}</span>
        <h2 className="dz-ai-h2">{data.title}</h2>
        <p className="dz-ai-lead">{data.lead}</p>
      </div>
      <div className="dz-ai-aud__grid">
        {data.cards.map((c, i) => (
          <article className="dz-ai-acard" key={i} style={{ '--ai-i': i }}>
            <span className="dz-ai-acard__mark" aria-hidden="true">{c.mark}</span>
            <div className="dz-ai-acard__body">
              <h3 className="dz-ai-acard__name">{c.name}</h3>
              <p className="dz-ai-acard__tag">{c.tagline}</p>
              <ul className="dz-ai-acard__list">
                {c.items.map((it, j) => (
                  <li key={j}>{it}</li>
                ))}
              </ul>
              <p className="dz-ai-acard__outcome">{c.outcome}</p>
            </div>
          </article>
        ))}
      </div>
      {data.ctaLink && <p className="dz-ai-cta-link" dangerouslySetInnerHTML={{ __html: data.ctaLink }} />}
    </section>
  );
}

/* ── ENGAGEMENT — 4 tracks + closing CTA ─────────────────────────────── */
function AiEngagementGlyph({ name }) {
  const c = { width: '36', height: '36', viewBox: '0 0 64 64', fill: 'none',
              stroke: 'currentColor', strokeWidth: '1.75',
              strokeLinecap: 'round', strokeLinejoin: 'round' };
  switch (name) {
    case 'compass': return (
      <svg {...c}>
        <circle cx="32" cy="32" r="22" />
        <polygon points="32,16 38,32 32,48 26,32" fill="currentColor" stroke="none" opacity="0.85">
          <animateTransform attributeName="transform" type="rotate"
                            from="0 32 32" to="360 32 32" dur="6s" repeatCount="indefinite" />
        </polygon>
        <circle cx="32" cy="32" r="3" fill="currentColor" stroke="none" />
      </svg>
    );
    case 'wrench': return (
      <svg {...c}>
        <path d="M40 12 a10 10 0 0 1 10 14 L26 50 a4 4 0 0 1 -6 -6 L44 20 a10 10 0 0 1 -4 -8 Z" />
        <circle cx="44" cy="20" r="2.5" fill="currentColor" stroke="none">
          <animate attributeName="opacity" values="1;0.4;1" dur="2s" repeatCount="indefinite" />
        </circle>
      </svg>
    );
    case 'package': return (
      <svg {...c}>
        <path d="M10 20 L32 10 L54 20 V44 L32 54 L10 44 Z" />
        <path d="M10 20 L32 30 L54 20" />
        <line x1="32" y1="30" x2="32" y2="54" opacity="0.6" />
        <circle cx="32" cy="22" r="2.5" fill="currentColor" stroke="none">
          <animate attributeName="opacity" values="1;0.4;1" dur="1.8s" repeatCount="indefinite" />
        </circle>
      </svg>
    );
    case 'platform': return (
      <svg {...c}>
        <rect x="8"  y="14" width="48" height="32" rx="2" />
        <line x1="8"  y1="22" x2="56" y2="22" opacity="0.55" />
        <circle cx="14" cy="18" r="1.4" fill="currentColor" stroke="none" />
        <circle cx="18" cy="18" r="1.4" fill="currentColor" stroke="none" />
        <circle cx="22" cy="18" r="1.4" fill="currentColor" stroke="none" />
        <line x1="14" y1="30" x2="30" y2="30" opacity="0.7" />
        <line x1="14" y1="36" x2="38" y2="36" opacity="0.5" />
        <rect x="42" y="28" width="10" height="10" fill="currentColor" stroke="none" opacity="0.45">
          <animate attributeName="opacity" values="0.45;0.85;0.45" dur="2s" repeatCount="indefinite" />
        </rect>
        <line x1="22" y1="46" x2="42" y2="46" />
      </svg>
    );
    default: return <svg {...c}><circle cx="32" cy="32" r="20" /></svg>;
  }
}

function AiEngagement({ data }) {
  const ref = useAiReveal(0.12);
  return (
    <section className="dz-ai-eng" ref={ref}>
      <div className="dz-ai-eng__head">
        <span className="dz-eyebrow dz-tms-eyebrow dz-tms-eyebrow--on-dark">{data.eyebrow}</span>
        <h2 className="dz-ai-h2 dz-ai-h2--on-dark">{data.title}</h2>
        <p className="dz-ai-lead dz-ai-lead--on-dark">{data.lead}</p>
      </div>
      <div className="dz-ai-eng__grid">
        {data.tracks.map((t, i) => (
          <article className="dz-ai-tcard" key={i} style={{ '--ai-i': i }}>
            <div className="dz-ai-tcard__glyph"><AiEngagementGlyph name={t.glyph} /></div>
            <div className="dz-ai-tcard__tag">{t.tag}</div>
            <h3 className="dz-ai-tcard__label">{t.label}</h3>
            <p className="dz-ai-tcard__text">{t.text}</p>
          </article>
        ))}
      </div>

      {data.cta && (
        <div className="dz-ai-eng__cta">
          <p className="dz-ai-eng__cta-line">{data.cta.line}</p>
          <a className="dz-ai-eng__cta-btn" href={data.cta.href}>
            {data.cta.label}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
          {data.cta.secondaryText && (
            <a className="dz-ai-eng__cta-secondary" href={data.cta.secondaryHref || '#'}>
              {data.cta.secondaryText}
            </a>
          )}
        </div>
      )}
    </section>
  );
}

/* ── EVIDENCE — proof bento clone with cited source notes (navy) ─────── */
function AiEvidence({ data }) {
  const ref = useAiReveal(0.15);
  return (
    <section className="dz-ai-proof" ref={ref}>
      <div className="dz-ai-proof__head">
        <span className="dz-eyebrow dz-tms-eyebrow dz-tms-eyebrow--on-dark">{data.eyebrow}</span>
        <h2 className="dz-ai-h2 dz-ai-h2--on-dark">{data.title}</h2>
        <p className="dz-ai-lead dz-ai-lead--on-dark">{data.lead}</p>
      </div>
      <div className="dz-ai-proof__grid">
        {data.metrics.map((m, i) => (
          <article className="dz-ai-mcard" key={i} style={{ '--ai-i': i }}>
            <AiCountNumeral value={m.numeral} className="dz-ai-mcard__num" />
            <div className="dz-ai-mcard__label">{m.label}</div>
            {m.sub && <div className="dz-ai-mcard__sub">{m.sub}</div>}
          </article>
        ))}
      </div>
      {data.notes && (
        <p className="dz-ai-lead dz-ai-lead--on-dark">
          {data.notes.map((n, i) => (
            <span key={i}>
              {i > 0 && '  ·  '}
              <a href={n.href} target="_blank" rel="noopener">{n.text}</a>
            </span>
          ))}
        </p>
      )}
    </section>
  );
}

/* ── FAQ — the questions are always listed; each answer opens in a <details>
   accordion and stays in the DOM so it is crawlable. ── */
function AiFaq({ data }) {
  const ref = useAiReveal(0.08);
  if (!data) return null;
  return (
    <section className="dz-section dz-faq" ref={ref} aria-label={data.sectionAriaLabel}>
      <div className="dz-section__inner">
        <span className="dz-eyebrow">{data.eyebrow}</span>
        <h2 className="dz-ai-h2">{data.title}</h2>
        <div className="dz-faq__list" id="dz-ai-faq-list">
          {data.items.map((it, i) => (
            <details key={i} className="dz-faq-item">
              <summary>{it.q}</summary>
              <div className="dz-faq-item__body">{it.a}</div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Page root ────────────────────────────────────────────────────────── */
function AiPage() {
  const data = window.DZ_AI;
  if (!data) {
    return (
      <main className="dz-ai dz-ai-missing">
        <h1>Ai · AI for BIM & digital engineering</h1>
        <p>{dzT('Content layer failed to load.', 'تعذّر تحميل طبقة المحتوى.')}</p>
      </main>
    );
  }
  return (
    <main className="dz-ai" id="dz-main">
      <AiHero       data={data.hero} />
      <AiProof      data={data.proof} />
      {data.projects && window.DzProjectCards && <window.DzProjectCards data={data.projects} id="ai-work" />}
      {data.apply    && <AiAudiences data={data.apply} />}
      {data.journey  && <AiJourney   data={data.journey} />}
      <AiEngagement data={data.engagement} />
      <AiAudiences  data={data.audiences} />
      {data.evidence && <AiEvidence  data={data.evidence} />}
      {data.whatIs   && <AiAudiences data={data.whatIs} />}
      {data.faq      && <AiFaq        data={data.faq} />}
    </main>
  );
}

window.AiPage = AiPage;
